import React, { memo, useState, useEffect, useCallback, useRef, useImperativeHandle, forwardRef, ForwardedRef, useMemo } from 'react'
import { Column, ContainerService, DragRowMove, Editors, FieldType, Formatter, Formatters, GridMenuEventWithElementCallbackArgs, GridOption, OnActiveCellChangedEventArgs, OnBeforeEditCellEventArgs, OnCellChangeEventArgs, OnClickEventArgs, OnDblClickEventArgs, OnDragEventArgs, OnEventArgs, OnHeaderClickEventArgs, OnKeyDownEventArgs, OnRenderedEventArgs, OnRowsChangedEventArgs, OnSelectedRowsChangedEventArgs, PaginationService, SingleColumnSort, SlickCellSelectionModel, SlickDataView, SlickGrid, SlickRowSelectionModel, SlickgridReact, SlickgridReactInstance, TranslaterService, addWhiteSpaces } from 'slickgrid-react';
import i18next, { type TFunction } from 'i18next';
import { Translation, WithTranslation, withTranslation } from 'react-i18next';
import $ from 'jquery'
import { useDebounceFn, useMount, useSize, useWhyDidYouUpdate } from 'ahooks';
import { debounce, throttle } from 'lodash-es';
import { message, Modal, Spin } from 'antd';
import styles from './index.module.less'
import clsx from 'clsx';
import { SlickgridEditDisabledClassName } from '@/config/constant';
import { SlickgridReactProps } from 'slickgrid-react/dist/types/components/slickgridReactProps';
import { traverseTree } from '@/utils/tree';
import ReactDOMServer from 'react-dom/server';
import { ReactComponent as ExpandIcon } from '@/assets/svg/slickGridFold.svg';
import { isNumeric } from '@/utils/number';

interface Props extends Partial<SlickgridReactProps> {
  loading: boolean
  disabled?: boolean
  showLoading?: boolean
  treeExpandColumn?: string // 指定哪一列显示展开图标
  treeLevels?: { key: number | string, label: string }[] // 层级数组
  loadChildren?: (item: any) => Promise<any[]> // 加载子节点的回调函数'
  expandAllLevel?: number // 展开全部子节点层级，用来控制展开全部
  quickEditEnable?: boolean // 是否启用快速编辑功能，默认为true
}

export interface SlickGridRef {
  reactGridInstance: SlickgridReactInstance | null;
  getReactGridInstance: () => SlickgridReactInstance | null;
  getActiveRowIndex: () => number | null;
  setActiveRowIndex: (rowIndex: number) => void;
  setActiveColumn: (columnIndex: number) => void;
  removeRowSelected: () => void;
  moveUp: () => void;
  moveDown: () => void;
  deleteRow: () => void;
  expandTree: (level: any) => void
  setCellDisabledStyle: ({ row, name, key }: { row: any, name: string, key: string }) => void
}

const QuickEditEditors = ['CustomTextEditor', 'number', 'float', 'input', 'FloatEditor']

const SlickGridComponent = forwardRef<SlickGridRef, Props>((props: Props, ref) => {
  const { loading, gridId, disabled, showLoading = true, treeExpandColumn, treeLevels = [], loadChildren, expandAllLevel = 0, quickEditEnable = false } = props

  // quickEditEnable 控制是否启用快速编辑功能：
  // - true（默认）：启用类似Excel的快速编辑，包括单击激活、双击编辑、键盘导航等
  // - false：禁用快速编辑，回退到原生SlickGrid编辑行为

  // 中文输入法状态标记
  const isComposing = useRef(false);

  // SlickGrid 实例引用
  const gridRef = useRef<SlickgridReactInstance | null>(null);

  // 当前激活的单元格元素引用
  const activeEditorRef = useRef<HTMLElement | null>(null);

  // DOM 变化监听器引用
  const mutationObserverRef = useRef<MutationObserver | null>(null);
  // 当前激活单元格的位置
  const activeCellPosition = useRef<{ row: number, cell: number } | null>(null);
  const stateRef = useRef<{
    activeColumn: any,// 当前高亮列
    activeRowIndex: any, // 当前高亮行
    loadingNodes: Set<string | number>, // 正在加载的节点ID集合
  }>({ activeColumn: undefined, activeRowIndex: undefined, loadingNodes: new Set() })
  const setActiveColumn = (v: any) => stateRef.current.activeColumn = v
  const getActiveColumn = () => stateRef.current.activeColumn
  const setActiveRowIndex = (v: any) => stateRef.current.activeRowIndex = v
  const getActiveRowIndex = () => stateRef.current.activeRowIndex
  // grid 实例
  const reactGridRef = useRef<SlickgridReactInstance | null>(null)
  // grid 对象
  const reactGridObjRef = useRef<SlickGrid | null>(null)
  // grid 视图
  const reactGridDataviewRef = useRef<SlickDataView | null>(null)
  // const size = useSize(document.querySelector('body'));
  // gridwrap ref
  const tableWrapRef = useRef<HTMLDivElement>(null)
  const warpSize = useSize(tableWrapRef.current)
  const gridOptions: GridOption = {
    enableColumnReorder: false, // 禁用列交换
    showHeaderRow: false,
    enableExcelCopyBuffer: true,
    enableCellNavigation: true, // 设置为 true 表示表格单元格可以被编辑。 设置为 true 表示可以使用键盘箭头键在单元格之间导航
    autoCommitEdit: true, // 是否自动提交编辑
    editable: true, // 设置为 true 表示表格单元格可以被编辑。 设置为 true 表示可以使用键盘箭头键在单元格之间导航
    i18n: i18next,
    enableTranslate: true,
    // enableRowSelection: true, // 启用行选择
    // autoResize: {
    //     container: '#container',
    //     // bottomPadding: 8,
    //     // maxHeight: 600,
    //     calculateAvailableSizeBy: 'container'
    // },
    gridMenu: {
      hideToggleFilterCommand: true, // 隐藏过滤菜单命令
      hideClearAllSortingCommand: true // 隐藏清除排序菜单命令
    },
    ...props.gridOptions,
    autoEdit: false, // 决定了表格单元格是否在点击后立即进入编辑模式，即双击进入编辑
    excelCopyBufferOptions: {
      ...props.gridOptions.excelCopyBufferOptions,
      onBeforePasteCell: (e, args) => {
        const grid = args.grid
        const curOptions = grid.getOptions();
        if (curOptions.editable) {
          return true
        }
        // 返回 false 将阻止粘贴操作
        console.log("onBeforePasteCell", curOptions.editable)
        return false;
      }
    },
    enableEmptyDataWarningMessage: true, // 启用空数据提示
    emptyDataWarning: {
      message: '没有数据可显示', // 自定义空数据提示文案
      // 可选：自定义样式
      messageKey: '暂无数据'
      // className: 'custom-empty-warning', // 自定义 CSS 类
    }
  }
  if (disabled) {
    gridOptions.editable = false
    // gridOptions.enableCellNavigation = false
    gridOptions.autoEdit = false
    gridOptions.autoCommitEdit = false
    // gridOptions.enableExcelCopyBuffer = false
  }

  // 向上移动。不考虑树形
  const moveUp = () => {
    if (!reactGridRef.current?.dataView) return;
    const selectedRows = reactGridRef.current?.slickGrid.getSelectedRows();
    const curRow = selectedRows[0]
    if (curRow > 0) {
      const item = reactGridRef.current?.dataView.getItem(curRow);
      console.log("item", item)
      if (item.__hasChildren) {
        message.warning("只能移动叶子节点")
        return;
      }
      reactGridRef.current?.dataView.deleteItem(item.id);
      reactGridRef.current?.dataView.insertItem(curRow - 1, item);
      // reactGridObjRef.current?.setSelectedRows([curRow - 1])
      // setActiveRowIndex(stateRef.current.activeRowIndex - 1)
    } else {
      message.warning("已经到顶了")
    }
  }

  // 向下移动。不考虑树形
  const moveDown = () => {
    if (!reactGridRef.current?.dataView) return;
    const selectedRows = reactGridRef.current?.slickGrid.getSelectedRows();
    const curRow = selectedRows[0]
    if (curRow < reactGridRef.current?.dataView?.getLength() - 1) {
      const item = reactGridRef.current?.dataView.getItem(curRow);
      if (item.__hasChildren) {
        message.warning("只能移动叶子节点")
        return;
      }
      reactGridRef.current?.dataView.deleteItem(item.id);
      reactGridRef.current?.dataView.insertItem(curRow + 1, item);
      reactGridObjRef.current?.setSelectedRows([curRow + 1])
      setActiveRowIndex(curRow + 1)
    }
  }

  // 删除行
  const deleteRow = () => {
    if (!reactGridRef.current?.dataView) return;
    const selectedRows = reactGridRef.current?.slickGrid.getSelectedRows();
    const curRow = selectedRows[0]
    const item = reactGridRef.current?.dataView.getItem(curRow);
    if (!item) {
      message.warning("请先选择一行")
      return;
    }
    Modal.confirm({
      title: `确定删除吗`,
      okText: '确认',
      cancelText: '取消',
      onOk() {
        reactGridRef.current?.dataView.deleteItem(item.id);
      },
      onCancel() { },
    })
  }
  // 移除行选中
  const removeRowSelected = () => {
    setActiveRowIndex(null) // 移除高亮行
    $(`#${gridId} .slick-cell`)?.removeClass("selected");
    $(`#${gridId} .slick-cell`)?.removeClass("slickgrid-selected");
    reactGridRef.current.slickGrid?.setSelectedRows([])
  }

  // 表格头click
  const handleTableHeaderClick = (e: CustomEvent<{ eventData: any; args: OnHeaderClickEventArgs; }>) => {
    const { eventData, args } = e.detail?.eventData;
    if (args.column.id) {
      const columnIndex = reactGridRef.current?.slickGrid.getColumnIndex(args.column.id);
      removeRowSelected()
      setActiveColumn(columnIndex)
      selectColumn(columnIndex)
      // 高亮列头
      $(`#${gridId} .slick-header-column`)?.removeClass('active');
      $(`#${gridId} div[data-id="${args.column.id}"].slick-header-column`)?.addClass('active');
    }
    props?.onHeaderClick?.(e)
  }

  // 选中列
  function selectColumn(columnIndex) {
    // 获取网格中的所有数据
    const data = reactGridRef.current?.dataView.getItems() || [];
    // 遍历所有行并选中指定列的单元格
    for (let i = 0; i < data.length; i++) {
      // 获取当前行的单元格
      const cell = reactGridRef.current?.slickGrid.getCellNode(i, columnIndex);
      // 如果单元格存在，则进行选中操作（例如添加CSS类）
      if (cell) {
        cell.classList.add('selected');
        cell.classList.add('slickgrid-selected');
      }
    }
  }
  // 取消选中列
  const cancelSelectColumn = () => {
    $(`#${gridId} .slick-cell`)?.removeClass("selected");
    $(`#${gridId} .slick-cell`)?.removeClass("slickgrid-selected");
    // 取消列头高亮
    $(`#${gridId} .slick-header-column`)?.removeClass('active');
    // reactGridRef.current.slickGrid?.setSelectedRows([])
  }

  // 完整的清除列选中状态
  const clearAllColumnSelection = () => {
    cancelSelectColumn();
    setActiveColumn(null);
  }

  // 视口滚动
  const handleViewPortChanged = throttle((e: CustomEvent<{ eventData: any; args: { grid: SlickGrid; }; }>) => {
    props.onViewportChanged?.(e)
    const activeColumn = stateRef.current.activeColumn
    console.log("activeColumn", activeColumn)
    if (activeColumn) {
      setActiveColumn(activeColumn)
      selectColumn(activeColumn)
    }
  }, 200)

  const reactGrid1Ready = (e: CustomEvent<any>) => {
    const reactGrid: SlickgridReactInstance = e.detail;
    reactGridRef.current = reactGrid
    reactGridObjRef.current = reactGrid && reactGrid.slickGrid;
    reactGridDataviewRef.current = reactGrid && reactGrid.dataView;
    // 禁止粘贴事件
    // e.detail.element.subscribe((e: any, args: any) => {
    //   e.preventDefault();
    //   e.stopPropagation();
    // });

    gridReady(reactGrid)

    e.detail.element.addEventListener('paste', (e: ClipboardEvent) => {
      const curOptions = reactGrid.slickGrid.getOptions();
      if (!curOptions.editable) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });
    // console.log("reactGrid1Ready", e)
    props?.onReactGridCreated?.(e)
  }

  // 保存单元格值到数据源
  // 参数：行号、列号、要保存的值
  const saveCellValueToDataSource = (row: number, cellIndex: number, value: string) => {
    console.log("saveCellValueToDataSource", row, cellIndex, value)
    if (!gridRef.current) return false;
    const grid = gridRef.current.slickGrid;

    const item = grid.getDataItem(row);
    const column = grid.getColumns()[cellIndex];

    if (item && column && item[column.field] !== value) {
      // 更新数据项
      item[column.field] = value;

      // 重要：使用 SlickGrid 的方法来更新数据源，确保内部状态同步
      // 这样排序、过滤等操作不会丢失编辑的数据
      const dataView = gridRef.current.dataView;
      if (dataView && dataView.updateItem) {
        dataView.updateItem(item.id, item);
      } else {
        // 如果没有数据视图，直接使用网格的数据更新方法
        grid.updateRow(row);
      }

      // 标记该行已更改，触发重新渲染
      grid.invalidateRow(row);
      grid.render();

      // 触发 onCellChange 事件通知外部组件
      setTimeout(() => {
        grid.onCellChange.notify({
          row: row,
          cell: cellIndex,
          item,
          column,
          grid
        });
      }, 0);

      return true;
    }
    return false;
  };

  // 网格初始化完成后的回调：设置各种事件监听
  const gridReady = (reactGrid: SlickgridReactInstance) => {
    gridRef.current = reactGrid;
    const grid = reactGrid.slickGrid;
    // 以下都是快速编辑的逻辑，禁用状态下不需要

    // 快速编辑功能被禁用时，不设置相关事件监听器
    if (!quickEditEnable) {
      console.log('🚫 快速编辑功能已禁用，使用原生SlickGrid编辑行为');
      return;
    }

    // 阻止SlickGrid默认编辑器启动，使用我们自定义的编辑逻辑
    grid.onBeforeEditCell.subscribe((e, args) => {
      // 检查当前列是否有 editor 配置
      const column = grid.getColumns()[args.cell];
      console.log('🔒 阻止默认编辑器启动，列:', column.name, '有editor:', !!column.editor);
      console.log("editBefore", QuickEditEditors.includes(column?.editor?.model?.name))
      // 如果是CustomTextEditor，使用我们自己的编辑逻辑
      // 其他类型的编辑器（下拉框、日期选择器等）仍使用原生逻辑
      if (QuickEditEditors.includes(column?.editor?.model?.name)) {
        return false; // 阻止默认编辑器
      }
      return true; // 允许原生编辑器
    });

    // 监听单元格激活变化：主要用于清理之前的编辑状态
    grid.onActiveCellChanged.subscribe((e, args) => {
      if (disabled) return; // 禁用状态下，不创建MutationObserver
      if (!args.cell && !args.row) return;
      const column = grid.getColumns()[args.cell];
      const isQuckTextEditor = QuickEditEditors.includes(column?.editor?.model?.name);
      if (isQuckTextEditor) {
        // 保存当前激活单元格的位置
        activeCellPosition.current = { row: args.row, cell: args.cell };
        const prevEditor = activeEditorRef.current;
        if (prevEditor) {
          // 在切换单元格前，确保之前单元格的数据已保存

          // 清理之前单元格的状态和事件监听器
          prevEditor.contentEditable = 'false';
          activeEditorRef.current = null;
        }

        // 🔧 关键修复：直接在这里绑定新单元格的事件，不依赖handleSelectedRowsChanged
        const newCell = grid.getCellNode(args.row, args.cell) as HTMLElement;
        if (newCell) {
          console.log('📌 通过onActiveCellChanged激活自定义编辑单元格:', newCell, column.name);
          activeEditorRef.current = newCell;
        }
        console.log('📍 单元格激活位置已更新:', args.row, args.cell);
      }
    });

    // 监听键盘事件：处理编辑状态下和非编辑状态下的不同按键行为
    grid.onKeyDown.subscribe((e, args) => {
      if (disabled) return; // 禁用状态下，不创建MutationObserver
      const activeCell = grid.getActiveCell();
      if (!activeCell) return;
      const cell = grid.getCellNode(activeCell.row, activeCell.cell) as HTMLElement;
      if (!cell) return;

      // 检查当前列的编辑器类型和编辑状态
      const column = grid.getColumns()[activeCell.cell];
      const isQuckTextEditor = QuickEditEditors.includes(column?.editor?.model?.name);
      if (isQuckTextEditor) {
        // 添加详细的按键调试信息
        console.log('🔧 keydown event', {
          key: e.key,
          keyCode: e.keyCode,
          isComposing: isComposing.current,
          isEditing: isQuckTextEditor && cell.classList.contains('editing'),
          isCustomTextEditor: isQuckTextEditor,
          editorType: column?.editor?.model?.name || 'none',
          timestamp: Date.now()
        });
      }

      let handled = false;
      // 其他编辑器不处理，让它们使用原生逻辑
      if (handled) {
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    });

    // 🔄 单击 vs 双击 的核心差异对比：
    // 
    // 📱 单击流程：
    // handleSelectedRowsChanged → bindCellEvents → contentEditable=true + focus()
    // ❌ 没有添加 'editing' class
    // 🎨 CSS: .slick-cell { caret-color: transparent; }
    // 🔍 结果：无光标，但能接收输入
    // 
    // 🖱️ 双击流程：  
    // onDblClick → startEditing → cell.classList.add('editing')
    // ✅ 添加了 'editing' class
    // 🎨 CSS: .slick-cell.editing { caret-color: black; }
    // 🔍 结果：有光标，且定位到文本末尾
    //
    // 💡 核心：'editing' class 的有无决定光标是否可见！

    // 监听双击事件：双击直接进入编辑状态（类似Excel）
    grid.onDblClick.subscribe((e, args) => {
      if (disabled) return; // 禁用状态下，不创建MutationObserver
      const cell = grid.getCellNode(args.row, args.cell) as HTMLElement;
      if (!cell) return;
      const column = grid.getColumns()[args.cell];
      if (QuickEditEditors.includes(column?.editor?.model?.name)) {
        e.preventDefault();
        e.stopPropagation();
        console.log('🖱️ 双击单元格进入编辑模式:', args.row, args.cell);
        // 确保单元格已激活
        grid.setActiveCell(args.row, args.cell);
      }
    })
  }

  // 单元格数据变化回调：更新显示的变化信息
  const handleCellChange = (e: CustomEvent<{ eventData: any; args: OnCellChangeEventArgs; }>) => {
    const { eventData, args } = e.detail;
    const { item, row, cell, column } = args
    console.log("单元格发生变化:", item)

    // 更新显示的改变值（用于UI展示）
    const newValue = item[column.field];
    // setState({ changedCellValue: `行${row + 1}, 列${column.name}: ${newValue}` });

    // 调用外部传入的onCellChange回调
    props?.onCellChange?.(e);
  }

  // 处理行选择变化事件：用于激活单元格后的编辑设置
  // 🖱️ 单击流程解析（为什么单击没有光标）：
  // 1. 用户单击单元格
  // 2. SlickGrid触发handleSelectedRowsChanged事件
  // 3. 调用bindCellEvents()设置contentEditable=true并focus()
  // 4. ⚠️ 关键：这里只是激活，没有调用startEditing()
  // 5. ⚠️ 所以单元格没有'editing' class
  // 6. CSS规则：.slick-cell { caret-color: transparent; } 生效
  // 7. 结果：单元格已聚焦可输入，但光标透明（看不见）
  const handleSelectedRowsChanged = (e: CustomEvent<{ eventData: any; args: OnSelectedRowsChangedEventArgs; }>) => {
    console.log("🚀 handleSelectedRowsChanged 被调用: ", e)
    // 快速编辑功能被禁用时，不处理快速编辑相关逻辑
    if (!quickEditEnable) {
      props?.onSelectedRowsChanged?.(e)
      return;
    }

    const { args: { rows, grid }, } = e.detail
    const activeCell = grid.getActiveCell()
    console.log("🔍 activeCell详情:", activeCell)
    if (!activeCell) {
      console.log("❌ 没有activeCell，退出")
      return;
    }

    // 获取激活的单元格DOM元素
    const cell = grid.getCellNode(activeCell.row, activeCell.cell) as HTMLElement;
    if (!cell) {
      console.log("❌ 没有找到cell DOM元素，退出")
      return;
    }

    // 检查当前列的编辑器类型
    const column = grid.getColumns()[activeCell.cell];
    console.log('🔍 handleSelectedRowsChanged 调试信息:', {
      columnName: column.name,
      editorModel: column?.editor?.model,
      editorModelName: column?.editor?.model?.name,
      QuickEditEditors,
      isMatch: QuickEditEditors.includes(column?.editor?.model?.name),
      cellContentEditable: cell.contentEditable
    });
    const isQuckTextEditor = QuickEditEditors.includes(column?.editor?.model?.name);
    if (isQuckTextEditor) {
      console.log('📌 通过行选择事件激活自定义编辑单元格:', cell, column.name);
      // 🔍 关键：只绑定事件，不进入编辑状态
      // bindCellEvents会设置contentEditable=true和focus()，让单元格能接收输入
      // 但是不会添加'editing' class，所以光标保持透明
      activeEditorRef.current = cell;
      activeCellPosition.current = { row: activeCell.row, cell: activeCell.cell };
    } else {
      console.log('📝 当前列使用原生编辑器或不可编辑:', column.name);
    }
    props?.onSelectedRowsChanged?.(e)
  }


  // 设置树形展开图标
  const setupTreeExpandIcon = () => {
    if (!reactGridRef.current || !treeExpandColumn) return;
    // 查找展开列
    const columns = reactGridRef.current.slickGrid.getColumns();
    const expandColumn = columns.find(col => col.id === treeExpandColumn);

    if (!expandColumn) return;

    // 修改列标题，添加展开图标
    const columnHeader = document.querySelector(`#${gridId} div[data-id="${treeExpandColumn}"].slick-header-column .slick-column-name`);
    if (!columnHeader) return;
    // 判断是否已经设置了 展开图标
    if (columnHeader.querySelector('.slg-header-arrow')) return;
    console.log("innerHTML exec")
    columnHeader.innerHTML = `<div class="slg-column-header">
                <span>${expandColumn.name}</span>
                <span class="slg-header-arrow"> ${ReactDOMServer.renderToStaticMarkup(<ExpandIcon />)}</span>
           </div>`

    const headerArrow = document.querySelector(`#${gridId} div[data-id="${treeExpandColumn}"].slick-header-column .slg-header-arrow`);
    if (headerArrow) {
      // 添加展开图标
      const expandIconHtml = ReactDOMServer.renderToString(<ExpandIcon />);
      headerArrow.innerHTML = expandIconHtml;

      let dropdownMenu = null;
      let hideTimeout = null;

      // 创建下拉菜单
      function createDropdownMenu() {
        if (dropdownMenu) return;

        dropdownMenu = document.createElement("ul");
        dropdownMenu.classList.add("slg-dropdown-menu");

        // 添加展开全部选项
        const expandAllItem = document.createElement("li");
        expandAllItem.textContent = "展开全部";
        expandAllItem.addEventListener("click", () => {
          expandTree(expandAllLevel);
        });
        dropdownMenu.appendChild(expandAllItem);

        // 添加层级选项
        treeLevels.forEach(level => {
          const li = document.createElement("li");
          li.textContent = level.label;
          li.addEventListener("click", () => {
            expandTree(level.key);
          });
          dropdownMenu.appendChild(li);
        });

        // 添加收起全部选项
        const collapseAllItem = document.createElement("li");
        collapseAllItem.textContent = "收起全部";
        collapseAllItem.addEventListener("click", () => {
          expandTree('all');
        });
        dropdownMenu.appendChild(collapseAllItem);

        document.body.appendChild(dropdownMenu);

        dropdownMenu.addEventListener("mouseenter", () => {
          clearTimeout(hideTimeout);
        });

        dropdownMenu.addEventListener("mouseleave", hideDropdown);
      }

      function showDropdown(event) {
        clearTimeout(hideTimeout);
        createDropdownMenu();

        const rect = event.target.getBoundingClientRect();
        dropdownMenu.style.top = `${rect.bottom + window.scrollY}px`;
        dropdownMenu.style.left = `${rect.left + window.scrollX}px`;
        dropdownMenu.style.display = "block";
      }

      function hideDropdown() {
        if (dropdownMenu) {
          dropdownMenu.remove();
          dropdownMenu = null;
        }
      }

      // 绑定事件
      headerArrow.addEventListener("mouseenter", showDropdown);
      headerArrow.addEventListener("mouseleave", () => {
        hideTimeout = setTimeout(hideDropdown, 200);
      });
    }
  }

  // 表格排序
  const handleTableBeforeSort = (e: CustomEvent<{ eventData: any; args: SingleColumnSort; }>) => {
    return false
  }

  // 行选中 样式
  const selectRowStyle = () => {
    const activeRow = getActiveRowIndex()
    // console.log("selectRowStyle", isNumeric(0), { activeRow, gridId })
    if (isNumeric(activeRow)) {
      // 只更新前后两行，避免每次 render 都全量操作所有 .slick-cell
      const lastRow = (stateRef.current as any)?.__lastStyledActiveRow;
      if (lastRow === activeRow) {
        // activeRow 未变时尽量跳过（避免 render 频繁触发）
        const cell = reactGridRef.current?.slickGrid.getCellNode(activeRow, 0);
        if (cell && $(cell).hasClass('selected')) return;
      }

      /**
       * 清理旧选中样式（只移除“已带选中 class”的单元格）
       * 说明：SlickGrid 有虚拟滚动，会复用行 DOM；仅按 row=lastRow 清理在极端情况下可能残留 class。
       * 这里改为精准清理，既避免全量扫描所有 .slick-cell，又不会残留。
       */
      $(`#${gridId} .slick-cell.active, #${gridId} .slick-cell.selected, #${gridId} .slick-cell.slickgrid-selected`)
        .removeClass('active selected slickgrid-selected');

      // 再设置当前行
      const cell = reactGridRef.current?.slickGrid.getCellNode(activeRow, 0);
      $(`#${gridId} .slick-row[row="${activeRow}"] .slick-cell`).addClass('selected slickgrid-selected');
      if (cell) {
        $(cell).addClass('active selected slickgrid-selected');
      }

      (stateRef.current as any).__lastStyledActiveRow = activeRow;

    }
  }

  // 清除列选中样式
  const clearColumnSelectedStyle = () => {
    // 清除列选中样式和状态
    clearAllColumnSelection();
  }

  // 表格click，不包括表头
  const handleTableBodyClick = (e: CustomEvent<{ eventData: any; args: OnClickEventArgs; }>) => {
    console.log("tableClick22", e)
    const detail = e.detail;
    const args = detail?.args;
    const eventData = detail?.eventData;
    clearColumnSelectedStyle() // 清空列选中。列头不触发TableClick
    setActiveColumn(null) // 清除列选中数据
    // const eventData = detail?.eventData;
    // const targetDom = eventData?.target as HTMLElement;
    if (args?.cell === 0) {
      // setTimeout(() => {


      // 清除列选中
      // setActiveColumn(null)
      // setActiveRowIndex(args?.row)
      // selectRowStyle()

      const isCell = reactGridRef.current.slickGrid.getSelectionModel() instanceof SlickCellSelectionModel;
      if (isCell) {
        reactGridRef.current.slickGrid.setSelectionModel(new SlickRowSelectionModel());
      }
      reactGridRef.current.slickGrid.invalidate();

      // }, 300);
      // setTimeout(() => {
      //     $('.slick-cell').removeClass('active selected');
      //     $(targetDom).addClass('active selected').siblings().addClass('selected');
      // }, 0);
    } else {
      // removeRowSelected()
      // const curOptions = reactGridRef.current.slickGrid.getOptions();
      // console.log('curOptions', curOptions)

      const isCell = reactGridRef.current.slickGrid.getSelectionModel() instanceof SlickRowSelectionModel;
      if (isCell) {
        console.log("SlickRowSelectionModel2")
        reactGridRef.current.slickGrid.setSelectionModel(new SlickCellSelectionModel());
        reactGridRef.current.slickGrid.invalidate();
      }
      // if (!disabled) {
      //   removeRowSelected()
      // }
      // props?.onClick?.(e)
    }

    // if (disabled) {
    //   eventData?.event?.stopPropagation()
    //   // 事件默认行为
    //   eventData?.event?.preventDefault()
    //   return;
    // };
    props?.onClick?.(e)
  }

  // 设置
  const setCellDisabledStyle = ({ row, name, key }: { row: string | number, name: string, key: string }) => {
    const cssStyleHash = {
      [row]: { [name]: SlickgridEditDisabledClassName }, // 第二行的 "name" 列应用 'highlight-cell' 样式
    };
    reactGridRef.current.slickGrid.setCellCssStyles(key, cssStyleHash);
  }


  const pageLoading = useMemo(() => {
    if (warpSize?.width < 10) return true // 适配 drawer
    return loading
  }, [loading, warpSize?.width])


  // 展开
  const expandTree = (level: any) => {
    console.log("level", level)
    const allItems = reactGridRef.current.dataView.getItems()
    if (level == 'all') { // 收起全部
      reactGridRef.current?.treeDataService.toggleTreeDataCollapse(true);
    }
    let exLevels = []
    if (level >= 1) {
      for (let i = 1; i <= level; i++) {
        exLevels.push(i - 1 + '')
      }
    }
    traverseTree(allItems, node => {
      if (level == 0) { // 展开全部
        node.__collapsed = false
      }

      if (level >= 1) {
        node.__collapsed = !exLevels.includes(String(node.__treeLevel))
      }

    })
    reactGridRef.current.dataView.setItems(allItems)
  }

  const handleTalbeRender = (e: CustomEvent<{ eventData: any; args: OnRenderedEventArgs; }>) => {
    props?.onRendered?.(e)
    if (treeExpandColumn) {
      setupTreeExpandIcon();
    }
    // 还需要渲染 当前行
    // NOTE: 应该直接改源码会好一些，这样写容易出问题
    // console.log("renderId", gridId)
    selectRowStyle()
  }

  useImperativeHandle(ref, () => {
    return {
      reactGridInstance: reactGridRef.current,
      getReactGridInstance: () => reactGridRef.current,
      getActiveRowIndex: () => {
        return stateRef.current.activeRowIndex
      },
      setActiveColumn,
      removeRowSelected,
      setActiveRowIndex,
      moveUp,
      moveDown,
      deleteRow,
      expandTree,
      setCellDisabledStyle
    }
  }, [])
  // 组件卸载时清理 MutationObserver
  useEffect(() => {
    return () => {
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
      }
    };
  }, []);

  console.log("pageLoading", pageLoading)

  useWhyDidYouUpdate('useWhyDidYouUpdateComponent', { ...props, });
  // return <div ref={tableWrapRef} className={styles['tableWrap']}>
  return <div className={styles['tableWrap']}>
    {(props.gridOptions && !pageLoading) ?
      <Translation>
        {
          t => <SlickgridReact
            {...props}
            onHeaderClick={handleTableHeaderClick}
            gridOptions={gridOptions}
            onSelectedRowsChanged={handleSelectedRowsChanged}
            onBeforeSort={handleTableBeforeSort}
            onViewportChanged={handleViewPortChanged}
            onClick={handleTableBodyClick}
            onReactGridCreated={reactGrid1Ready}
            onRendered={handleTalbeRender}
            onCellChange={handleCellChange}
          />
        }
      </Translation> : null}

    {/* <div className={styles['diagonal-grid-layer']}></div> */}

    {showLoading ? <div className={clsx(pageLoading ? '' : 'z-index--1', styles['spin'])}>
      <Spin spinning={loading}> </Spin>
    </div> : null}
  </div>
})
function shouldMemo(prevProps, nextProps) {
  /*
  如果把 nextProps 传入 render 方法的返回结果与
  将 prevProps 传入 render 方法的返回结果一致则返回 true，
  否则返回 false
  */

  // console.log("prevProps.columnDefinitions", prevProps.dataset === nextProps.dataset)
  const result = (prevProps.loading === nextProps.loading)
    && (prevProps.dataset === nextProps.dataset)
    && (prevProps.datasetHierarchical === nextProps.datasetHierarchical)
    // && (prevProps.columnDefinitions?.map(i => (i.id + i.cssClass))?.join('') === nextProps.columnDefinitions?.map(i => (i.id + i.cssClass))?.join(''))
    && (prevProps.columnDefinitions?.map(i => (i ? i.id + i.cssClass : ''))?.join('') === nextProps.columnDefinitions?.map(i => (i ? i.id + i.cssClass : ''))?.join(''))
  // console.log("shouldMemo", prevProps.columnDefinitions, nextProps.columnDefinitions, (prevProps.columnDefinitions?.map(i => i.id)?.join('') === nextProps.columnDefinitions?.map(i => i.id)?.join('')))
  return result
}
export default React.memo(SlickGridComponent, shouldMemo)
// export default SlickGridComponent
